import type { EpochMs, FrameRate } from './types.ts';
import { localDateString, zonedParts, zonedWallTimeToUtc } from './tz.ts';

export interface FrameRateInfo {
  id: FrameRate;
  /** Exact rate, e.g. 24000/1001. */
  fps: number;
  /** Integer frames-per-second label used for counting (24, 30, 60…). */
  nominal: number;
  dropFrame: boolean;
  label: string; // "23.976", "29.97 DF"
}

export const FRAME_RATES: readonly FrameRateInfo[] = Object.freeze([
  { id: '23.976', fps: 24000 / 1001, nominal: 24, dropFrame: false, label: '23.976' },
  { id: '24', fps: 24, nominal: 24, dropFrame: false, label: '24' },
  { id: '25', fps: 25, nominal: 25, dropFrame: false, label: '25' },
  { id: '29.97', fps: 30000 / 1001, nominal: 30, dropFrame: false, label: '29.97 NDF' },
  { id: '29.97DF', fps: 30000 / 1001, nominal: 30, dropFrame: true, label: '29.97 DF' },
  { id: '30', fps: 30, nominal: 30, dropFrame: false, label: '30' },
  { id: '48', fps: 48, nominal: 48, dropFrame: false, label: '48' },
  { id: '50', fps: 50, nominal: 50, dropFrame: false, label: '50' },
  { id: '59.94', fps: 60000 / 1001, nominal: 60, dropFrame: false, label: '59.94 NDF' },
  { id: '59.94DF', fps: 60000 / 1001, nominal: 60, dropFrame: true, label: '59.94 DF' },
  { id: '60', fps: 60, nominal: 60, dropFrame: false, label: '60' },
].map((r) => Object.freeze(r as FrameRateInfo)));

const RATE_MAP = new Map<string, FrameRateInfo>(FRAME_RATES.map((r) => [r.id, r]));

export function frameRateInfo(rate: FrameRate): FrameRateInfo {
  const r = RATE_MAP.get(rate);
  if (!r) throw new Error(`Unknown frame rate: ${String(rate)}`);
  return r;
}

export interface Timecode {
  hh: number;
  mm: number;
  ss: number;
  ff: number;
  dropFrame: boolean;
  /** "HH:MM:SS:FF" (";" before frames for drop-frame). */
  text: string;
}

const p2 = (n: number) => String(n).padStart(2, '0');

function makeTimecode(hh: number, mm: number, ss: number, ff: number, dropFrame: boolean): Timecode {
  return { hh, mm, ss, ff, dropFrame, text: `${p2(hh)}:${p2(mm)}:${p2(ss)}${dropFrame ? ';' : ':'}${p2(ff)}` };
}

/** Frame numbers dropped per minute for a DF rate (2 at 30, 4 at 60). */
function dropPerMinute(info: FrameRateInfo): number {
  return info.dropFrame ? Math.round(info.nominal / 15) : 0;
}

/** Timecode frames in one 24-hour day at `rate` (DF aware). */
export function framesPerDay(rate: FrameRate): number {
  const info = frameRateInfo(rate);
  const drop = dropPerMinute(info);
  return info.nominal * 86400 - drop * (1440 - 144);
}

/**
 * Wall-clock-aligned time-of-day reference ("slate" timecode).
 * HH:MM:SS always equals the wall clock in the chosen zone (UTC by default) — never drifts;
 * FF = floor(fraction-of-second × nominal fps). This is a visual sync reference, not SMPTE LTC.
 *
 * Drop-frame rates: the separator is ';' so the slate reads like the camera, but because the
 * slate is locked to the wall clock no frame numbers are skipped (e.g. 00:01:00;00 is shown).
 */
export function timeOfDayTimecode(t: EpochMs, rate: FrameRate, opts?: { timezone?: string }): Timecode {
  const info = frameRateInfo(rate);
  const p = zonedParts(t, opts?.timezone ?? 'UTC');
  const ff = Math.min(info.nominal - 1, Math.floor((p.millisecond / 1000) * info.nominal));
  return makeTimecode(p.hour, p.minute, p.second, ff, info.dropFrame);
}

/** SMPTE frame count → timecode (proper drop-frame math) — for clip durations / source TC. Wraps at 24 h. */
export function framesToTimecode(frames: number, rate: FrameRate): Timecode {
  const info = frameRateInfo(rate);
  if (!Number.isFinite(frames)) throw new Error(`Invalid frame count: ${frames}`);
  const perDay = framesPerDay(rate);
  let n = Math.floor(frames) % perDay;
  if (n < 0) n += perDay;
  const nom = info.nominal;
  if (info.dropFrame) {
    const drop = dropPerMinute(info);
    const perMin = nom * 60 - drop;
    const per10 = nom * 600 - drop * 9;
    const d = Math.floor(n / per10);
    const m = n % per10;
    n += drop * 9 * d + (m > drop ? drop * Math.floor((m - drop) / perMin) : 0);
  }
  const ff = n % nom;
  const totalSec = Math.floor(n / nom);
  return makeTimecode(Math.floor(totalSec / 3600) % 24, Math.floor(totalSec / 60) % 60, totalSec % 60, ff, info.dropFrame);
}

const TC_RE = /^\s*(\d{1,2})[:](\d{2})[:](\d{2})([:;.,])(\d{2,3})\s*$/;

function parseTimecode(text: string, rate: FrameRate): { hh: number; mm: number; ss: number; ff: number; info: FrameRateInfo } {
  const info = frameRateInfo(rate);
  if (typeof text !== 'string') throw new Error('Timecode must be a string');
  const m = TC_RE.exec(text);
  if (!m) throw new Error(`Invalid timecode "${text}" (expected HH:MM:SS:FF or HH:MM:SS;FF)`);
  const hh = Number(m[1]);
  const mm = Number(m[2]);
  const ss = Number(m[3]);
  const ff = Number(m[5]);
  if (hh > 23 || mm > 59 || ss > 59) throw new Error(`Invalid timecode "${text}": field out of range`);
  if (ff >= info.nominal) throw new Error(`Invalid timecode "${text}": frame ${ff} ≥ ${info.nominal} at ${info.label}`);
  return { hh, mm, ss, ff, info };
}

/**
 * Parse "HH:MM:SS:FF" or "HH:MM:SS;FF" (either separator accepted for any rate) → frame count.
 * For drop-frame rates, frame labels that do not exist (e.g. 00:01:00;00 at 29.97 DF) throw.
 */
export function timecodeToFrames(text: string, rate: FrameRate): number {
  const { hh, mm, ss, ff, info } = parseTimecode(text, rate);
  const nom = info.nominal;
  let frames = (hh * 3600 + mm * 60 + ss) * nom + ff;
  if (info.dropFrame) {
    const drop = dropPerMinute(info);
    if (ss === 0 && mm % 10 !== 0 && ff < drop) {
      throw new Error(`Invalid drop-frame timecode "${text}": frames 00–${p2(drop - 1)} are dropped at minute ${mm}`);
    }
    const totalMin = hh * 60 + mm;
    frames -= drop * (totalMin - Math.floor(totalMin / 10));
  }
  return frames;
}

/**
 * Interpret a camera's time-of-day source timecode (e.g. "17:42:10:12") as a UTC instant on the
 * mission date. `timezone` is the zone the camera clock was jammed to ('UTC' or an IANA zone);
 * `nearT` disambiguates the calendar day (choose the date that puts the result closest to nearT).
 *
 * Inverse of `timeOfDayTimecode`: HH:MM:SS is wall-clock time, FF adds ff/nominal seconds
 * (drop-frame labels are treated the same way, i.e. wall-clock aligned).
 */
export function timeOfDayTimecodeToUtc(text: string, rate: FrameRate, nearT: EpochMs, timezone: string): EpochMs {
  const { hh, mm, ss, ff, info } = parseTimecode(text, rate);
  const ms = Math.round((ff * 1000) / info.nominal);
  const base = localDateString(nearT, timezone);
  const [y, mo, d] = base.split('-').map(Number) as [number, number, number];
  let best = NaN;
  for (const off of [-1, 0, 1]) {
    const date = new Date(Date.UTC(y, mo - 1, d + off));
    const t = zonedWallTimeToUtc(
      { year: date.getUTCFullYear(), month: date.getUTCMonth() + 1, day: date.getUTCDate(), hour: hh, minute: mm, second: ss, millisecond: ms },
      timezone,
    );
    if (Number.isNaN(best) || Math.abs(t - nearT) < Math.abs(best - nearT)) best = t;
  }
  return best;
}

/**
 * UTC clock text used by the slate: "2026-09-29T17:42:10Z", or with `withMillis`
 * "2026-09-29T17:42:10.500Z". Default: no millis.
 */
export function formatUtcClock(t: EpochMs, withMillis = false): string {
  const iso = new Date(t).toISOString();
  return withMillis ? iso : `${iso.slice(0, 19)}Z`;
}

/**
 * A camera's timecode at `t` (Field UX spec §9): time-of-day slate TC in `timezone` (default UTC)
 * shifted by the camera's jam offset `tcOffsetFrames` (camera TC − slate TC, in frames at `rate`).
 * Wall-clock aligned like `timeOfDayTimecode` (no drop-frame label skipping); wraps at 24 h.
 * `tcSource` 'EXT' (external sync box) is reserved and treated like TOD + offset.
 */
export function cameraTimecodeAt(
  camera: { tcOffsetFrames?: number | null },
  t: EpochMs,
  rate: FrameRate,
  timezone = 'UTC',
): Timecode {
  const tod = timeOfDayTimecode(t, rate, { timezone });
  const off = Math.round(camera.tcOffsetFrames ?? 0);
  if (off === 0) return tod;
  const info = frameRateInfo(rate);
  const perDay = info.nominal * 86400;
  let n = ((tod.hh * 3600 + tod.mm * 60 + tod.ss) * info.nominal + tod.ff + off) % perDay;
  if (n < 0) n += perDay;
  const ff = n % info.nominal;
  const sec = Math.floor(n / info.nominal);
  return makeTimecode(Math.floor(sec / 3600), Math.floor(sec / 60) % 60, sec % 60, ff, info.dropFrame);
}
